// @ts-check
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminPosOrderService from '../services/adminPosOrderService';
import { buildCampaignLink } from '../utils/campaignLink';
import { STOREFRONT_URL } from '../utils/storefront';
import PosProductPicker from './PosProductPicker';
import Modal from './ui/Modal';

const inputClass =
  'w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground';

/**
 * Builds the shareable link for a discount code: pick the page it lands on (a product, or the
 * homepage), tag it for tracking, copy it. The storefront applies the code when the link is opened.
 * @param {{code: string, onClose: () => void}} props
 */
const DiscountLinkModal = ({ code, onClose }) => {
  const toast = useToast();
  const [products, setProducts] = useState(/** @type {any[]} */ ([]));
  const [product, setProduct] = useState(/** @type {any} */ (null));
  const [source, setSource] = useState('instagram');
  const [medium, setMedium] = useState('influencer');
  const [campaign, setCampaign] = useState(code.toLowerCase());

  useEffect(() => {
    adminPosOrderService
      .getCatalog()
      .then(({ products: list }) => setProducts(list))
      .catch((err) => toast.error(err.message || 'Failed to load products'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const link = STOREFRONT_URL
    ? buildCampaignLink({ origin: STOREFRONT_URL, code, productSlug: product?.slug, source, medium, campaign })
    : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy - select the link and copy it by hand');
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="lg" fullHeight={false}>
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-xl font-serif">Link for {code}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Anyone who opens this link gets the code applied automatically at checkout.
        </p>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Opens on</label>
          {product ? (
            <div className="flex items-center justify-between border border-input rounded-lg px-3 py-2 text-sm">
              <span>{product.name}</span>
              <button onClick={() => setProduct(null)} className="text-muted-foreground hover:text-foreground">
                Change
              </button>
            </div>
          ) : (
            <>
              <PosProductPicker products={products} onPick={setProduct} />
              <p className="text-xs text-muted-foreground mt-1">No piece chosen: the link opens the homepage.</p>
            </>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Source</label>
            <input className={inputClass} value={source} onChange={(e) => setSource(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Medium</label>
            <input className={inputClass} value={medium} onChange={(e) => setMedium(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Campaign</label>
            <input className={inputClass} value={campaign} onChange={(e) => setCampaign(e.target.value)} />
          </div>
        </div>
        {link ? (
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Your link</label>
            <textarea
              readOnly
              rows={3}
              value={link}
              onFocus={(e) => e.target.select()}
              className={`${inputClass} font-mono text-xs`}
            />
          </div>
        ) : (
          <p className="text-sm text-red-600">REACT_APP_STOREFRONT_URL is not set, so the link cannot be built.</p>
        )}
      </div>
      <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3">
        <button onClick={onClose} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
          Close
        </button>
        <button
          onClick={copy}
          disabled={!link}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Copy size={16} />
          Copy link
        </button>
      </div>
    </Modal>
  );
};

export default DiscountLinkModal;
